export const { REACT_APP_BASE_HOST, REACT_APP_ACR_API_BASE_URL } = process.env;
export const REACT_APP_ACR_API_PROTOCOL = (process.env.REACT_APP_ACR_API_PROTOCOL || 'https').replace('://', '');
export const BASE_URL = `${REACT_APP_BASE_HOST}`;
export const HOME_URL = `/`;
export const CONTACT_URL = '/contact';
export const BASE_AUTH_URL = `/api/auth`
export const PRODUCT_SUBMIT_URL = `/product/submit`;
export const PRODUCT_DETAIL_URL = `/product-details/:productId`;
export const PRODUCT_LIST_SUBMIT_URL = `/for-buyers`; // Points to for-buyers page
export const PRODUCT_EDIT_URL = `/product/edit`
export const PRODUCT_BASE_URL = `/product`;
export const VENDOR_BASE_URL = `/vendor`;
export const VENDOR_OVERVIEW_URL = `/vendor/overview`;
export const VENDOR_EDIT_URL = `/vendor/edit`;
export const VENDOR_DASHBOARD_URL = `/vendor/dashboard`;
export const ORGANIZATION_CREATE_URL = `/organization/create`;
export const ANALYTICS_URL = `/analytics`;
export const SUPERUSER_TOOLS_URL = `/superuser-tools`;
export const ENTITY_APPROVAL_URL = `/approval`;
export const ACCOUNT_FIRST_TIME_URL = '/account/first-time';
export const WELCOME_URL = '/welcome';
export const FOR_BUYERS_URL = '/for-buyers';
export const FOR_VENDORS_URL = '/for-vendors';
export const RESOURCES_URL = '/resources';
export const TERMS_OF_USE_URL = '/terms-of-use';
export const REQUEST_LINK_MESSAGE = 'Contact for Details';
export const HYPERLINK_LIST_URL = '/hyperlink-list';
export const CALLBACK_URL = '/callback';

// ACR Editor routes
export const ACR_EDITOR_BASE_URL = '/acr-editor';
export const ACR_EDITOR_ABOUT_URL = `${ACR_EDITOR_BASE_URL}/about`;
export const ACR_EDITOR_CHAPTER_URL = `${ACR_EDITOR_BASE_URL}/chapter`;
export const ACR_EDITOR_REPORT_URL = `${ACR_EDITOR_BASE_URL}/report`;
export const ACR_EDITOR_GLOSSARY_URL = `${ACR_EDITOR_BASE_URL}/glossary`;
export const ACR_EDITOR_OVERVIEW_URL = `${ACR_EDITOR_BASE_URL}/overview`;

export const ENTITY_TYPE_VENDOR = 'vendor';
export const ENTITY_TYPE_AGENCY = 'agency';

/**
 * Shown when an ACR author selects a product that is not part of their
 * organization ("unknown product"). Such reports can be built and downloaded,
 * but cannot be autosaved to or submitted into the repository.
 */
export const UNKNOWN_PRODUCT_WARNING = "This product isn't in your organization's listings. You can build and download an ACR for it, but it can't be autosaved or submitted to the repository. To save or submit, choose one of your organization's products or add it as a new product.";

/**
 * Short status shown in the ACR Editor's autosave banner slot while the author works on an
 * unknown product (one not in their organization's listings). Autosave is intentionally off.
 */
export const UNKNOWN_PRODUCT_AUTOSAVE_STATUS = "Autosave is off - this product isn't in your organization's listings. You can still download this ACR, but it can't be autosaved or submitted.";

/**
 * Short status shown in the ACR Editor's autosave banner slot while the author has an unsaved new
 * product open (e.g. right after logging in from Public Mode). Autosave and submit stay off until
 * the product exists on the server, so this explains why on every editor page, not just About.
 */
export const ADD_NEW_PRODUCT_AUTOSAVE_STATUS = "Autosave is off until your new product is saved. Save or submit it on the About page to turn on autosave and submitting.";

/**
 * Shown to anonymous (not logged-in) users using the ACR Editor in Public Mode. They can build and
 * download an ACR, but autosave and submit are disabled because there is no session to persist to.
 */
export const PUBLIC_EDITOR_MODE_MESSAGE = "You are using the ACR Editor in Public Mode. Autosave and submitting are disabled, but you can still download your final ACR. If you want to save the ACR in ACR Repository, please log in.";

export const JWT_TOKEN_KEY = "jwt";
export const ACTIVITY_STORAGE_KEY = "acr_last_activity";
/**
 * sessionStorage key caching the active SuperUser impersonation session for fast restore on reload.
 * The server (GET /api/impersonation/current) is the source of truth; this is only an optimistic hint.
 */
export const IMPERSONATION_SESSION_KEY = "acr_impersonation";
/**
 * sessionStorage key recording the last automatic silent re-authentication attempt (timestamp).
 * Used to detect and break an auth redirect loop when a token keeps getting rejected.
 */
export const REAUTH_ATTEMPT_STORAGE_KEY = "acr_reauth_attempt_ts";
export const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;
export const INACTIVITY_WARNING_DURATION_MS = 5 * 60 * 1000;
/** How long before a Support Mode session's absolute expiry to warn the SuperUser to save. */
export const SUPPORT_MODE_WARNING_DURATION_MS = 5 * 60 * 1000;
/**
 * How long before the session token's `exp` to proactively (and silently) re-authenticate an
 * ACTIVE user, so a fresh token is in hand before a request would 401. Idle users are left to the
 * inactivity-logout flow. Must be comfortably shorter than the Login.gov id_token lifetime.
 */
export const SESSION_EXPIRY_REAUTH_LEAD_MS = 60 * 1000;
/** App user account lifecycle (API `AccountStatus` / `AppUser.status`). */
export const ACCOUNT_STATUS_ACTIVE = 'Active';
export const ACCOUNT_STATUS_INACTIVE = 'Inactive';
export const ACCOUNT_STATUS_REVOKED = 'Revoked';
export const ACCOUNT_STATUS_PENDING = 'Pending';

export const ACCESS_LEVEL_SUPER_USER = 'SUPERUSER';
export const ACCESS_LEVEL_ADMIN = 'ADMIN';
/** Type-2 (non-admin) access tier; aligns with backend IsUser policy. */
export const ACCESS_LEVEL_USER = 'USER';

export const STATUS_OKAY = 'okay';
export const STATUS_WAITING = 'waiting';
export const STATUS_DENIED = 'denied';

export const ACCESS_NO_LOGIN = 'nologin';
export const ACCESS_DENIED = 'denied';
export const ACCESS_GRANTED = 'granted';
export const ACCESS_WAITING = 'waiting';

export const NO_VENDOR_FOUND = 'Vendor not found.';

export const PRODUCT_VISIBILITY_PUBLIC = "Public";
export const PRODUCT_VISIBILITY_BUYERS_ONLY = "BuyersOnly";
export const PRODUCT_VISIBILITY_HIDE = "Hide";

/**
 * ACR Editor product-selection UI states, stored on `evaluation.product_ui_state`. Shared here so
 * the editor shell and the About product form agree on the vocabulary — e.g. graduating a Public
 * Mode download-only draft into the Add-New-Product flow when the author logs in mid-draft.
 * - SEARCH: looking up / choosing an org product (default).
 * - SELECTED_EXISTING: an existing org product is attached.
 * - ADD_NEW: entering a brand-new product to save/submit.
 * - UNKNOWN: a typed product outside the author's org — download-only (no autosave/submit).
 */
export const PRODUCT_UI_STATE_SEARCH = "search";
export const PRODUCT_UI_STATE_SELECTED_EXISTING = "selected_existing";
export const PRODUCT_UI_STATE_ADD_NEW = "add_new";
export const PRODUCT_UI_STATE_UNKNOWN = "unknown";

export const stateLabelValues = [
  { 'label':'Alabama', 'value': 'AL' },  { 'label':'Alaska', 'value': 'AK'},  { 'label':'American Samoa', 'value': 'AS'},
  { 'label':'Arizona', 'value': 'AZ'},  { 'label':'Arkansas', 'value': 'AR'},  { 'label':'California', 'value': 'CA'},
  { 'label':'Colorado', 'value': 'CO'},  { 'label':'Connecticut', 'value': 'CT'},  { 'label':'Delaware', 'value': 'DE'},
  { 'label':'District of Columbia', 'value': 'DC'},  { 'label':'States of Micronesia', 'value': 'FM'},  { 'label':'Florida', 'value': 'FL'},
  { 'label':'Georgia', 'value': 'GA'},  { 'label':'Guam', 'value': 'GU'},  { 'label':'Hawaii', 'value': 'HI'},
  { 'label':'Idaho', 'value': 'ID'},  { 'label':'Illinois', 'value': 'IL'},  { 'label':'Indiana', 'value': 'IN'},
  { 'label':'Iowa', 'value': 'IA'},  { 'label':'Kansas', 'value': 'KS'},  { 'label':'Kentucky', 'value': 'KY'},
  { 'label':'Louisiana', 'value': 'LA'},  { 'label':'Maine', 'value': 'ME'},  { 'label':'Marshall Islands', 'value': 'MH'},
  { 'label':'Maryland', 'value': 'MD'},  { 'label':'Massachusetts', 'value': 'MA'},  { 'label':'Michigan', 'value': 'MI'},
  { 'label':'Minnesota', 'value': 'MN'},  { 'label':'Mississippi', 'value': 'MS'},  { 'label':'Missouri', 'value': 'MO'},
  { 'label':'Montana', 'value': 'MT'},  { 'label':'Nebraska', 'value': 'NE'},  { 'label':'Nevada', 'value': 'NV'},
  { 'label':'New Hampshire', 'value': 'NH'},  { 'label':'New Jersey', 'value': 'NJ'},  { 'label':'New Mexico', 'value': 'NM'},
  { 'label':'New York', 'value': 'NY'},  { 'label':'North Carolina', 'value': 'NC'},  { 'label':'North Dakota', 'value': 'ND'},
  { 'label':'Northern Mariana Islands', 'value': 'MP'},  { 'label':'Ohio', 'value': 'OH'},  { 'label':'Oklahoma', 'value': 'OK'},
  { 'label':'Oregan', 'value': 'OR'},  { 'label':'Palau', 'value': 'PW'},  { 'label':'Pennsilvania', 'value': 'PA'},
  { 'label':'Puerto Rico', 'value': 'PR'},  { 'label':'Rhode Island', 'value': 'RI'},  { 'label':'South Carolina', 'value': 'SC'},
  { 'label':'South Dakota', 'value': 'SD'},  { 'label':'Tennessee', 'value': 'TN'},  { 'label':'Texas', 'value': 'TX'},
  { 'label':'Utah', 'value': 'UT'},  { 'label':'Vermont', 'value': 'VT'},  { 'label':'Virgin Islands', 'value': 'VI'},
  { 'label':'Virginia', 'value': 'VA'},  { 'label':'Washington', 'value': 'WA'},  { 'label':'West Virginia', 'value': 'WV'},
  { 'label':'Wisconsin', 'value': 'WI'},  { 'label':'Wyoming', 'value': 'WY'}
];

export const PAGE_SIZE = 20;
export const BASE_TITLE = 'ACR Repository';

// Notification URLs and Types
export const NOTIFICATIONS_URL = '/notifications';
export const NOTIFICATION_TYPE_PENDING_ACCOUNT_REQUEST = 'PENDING_ACCOUNT_REQUEST';
export const NOTIFICATION_TYPE_PENDING_ACCOUNT_REQUEST_REMINDER = 'PENDING_ACCOUNT_REQUEST_REMINDER';
export const NOTIFICATION_TYPE_ACCOUNT_ROLE_ASSIGNMENT = 'ACCOUNT_ROLE_ASSIGNMENT';
export const NOTIFICATION_TYPE_ACCOUNT_REQUEST_DENIED = 'ACCOUNT_REQUEST_DENIED';
export const NOTIFICATION_TYPE_PENDING_ACCOUNT_DEACTIVATION = 'PENDING_ACCOUNT_DEACTIVATION';
export const NOTIFICATION_TYPE_ACCOUNT_DEACTIVATED = 'ACCOUNT_DEACTIVATED';